import React, { useState, useEffect } from 'react';
import { trpc } from 'src/trpc';
import { DataTable } from 'src/components/table/DataTable';
import { SortingState } from '@tanstack/react-table';
import { ContainerType } from 'src/trpc';
import { Search } from 'src/components/Search';
import { useSearchParams } from 'react-router-dom';
import { ContainerTypeColumns } from 'src/pages/Settings/columns/ContainerTypeColumns';
import { ContainerTypeSheet } from 'src/pages/Settings/container-type/ContainerTypeSheet';
import { Button } from '@/components/ui/button';
import { MoneyFromCents } from '@libs/services/money';
import { useDebounce } from 'src/components/Debounce';
import { useOrganization } from 'src/context/OrganizationContext';
import { Loader } from 'src/components/Loader';
import { keepPreviousData } from '@tanstack/react-query';

interface ContainerTypeWithActions extends Omit<ContainerType, 'pricePerDay' | 'preAuthAmount'> {
  pricePerDay: number;
  preAuthAmount: number;
  displayPricePerDay: string;
  displayPreAuthAmount: string;
  onEdit: (containerType: ContainerType) => void;
}

const ContainerTypesTab: React.FC = () => {
  const [containerTypes, setContainerTypes] = useState<ContainerTypeWithActions[]>([]);
  const [editingContainerType, setEditingContainerType] = useState<ContainerType | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [params, setParams] = useSearchParams({
    offset: '0',
    limit: '50',
    search: '',
  });

  const [search, setSearch] = useState(params.get('search') ?? '');
  const debouncedSearch = useDebounce(search, 500);

  const offset = parseInt(params.get('offset') ?? '0');
  const limit = parseInt(params.get('limit') ?? '50');
  const currentPage = offset === 0 ? 1 : offset / limit + 1;

  // Use organization context for org-dependent queries
  const { organizationId, isResolved, isOrganizationSwitching, setIsOrganizationSwitching } =
    useOrganization();

  const containerTypeQuery = trpc.admin.containerType.getAllContainerTypes.useQuery(
    {
      offset,
      limit,
      search: params.get('search') || '',
    },
    {
      placeholderData: keepPreviousData,
      enabled: isResolved && !!organizationId,
    },
  );

  // Reset organization switching state when fetch completes
  useEffect(() => {
    if (isOrganizationSwitching && !containerTypeQuery.isFetching) {
      setIsOrganizationSwitching(false);
    }
  }, [isOrganizationSwitching, containerTypeQuery.isFetching, setIsOrganizationSwitching]);

  useEffect(() => {
    if (containerTypeQuery.data) {
      const containerTypesWithEditAction: ContainerTypeWithActions[] =
        containerTypeQuery.data.containerTypes.map((containerType) => ({
          ...containerType,
          displayPricePerDay: MoneyFromCents(containerType.pricePerDay).toFormat(),
          displayPreAuthAmount: MoneyFromCents(containerType.preAuthAmount).toFormat(),
          onEdit: (containerType: ContainerType) => setEditingContainerType(containerType),
        }));
      setContainerTypes(containerTypesWithEditAction);
    }
  }, [containerTypeQuery.data]);

  useEffect(() => {
    const currentSearch = params.get('search') ?? '';
    if (debouncedSearch !== currentSearch) {
      const newParams = new URLSearchParams(params);
      newParams.set('search', debouncedSearch);
      newParams.set('offset', '0');
      setParams(newParams);
    }
  }, [debouncedSearch, params, setParams]);

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const handlePageChange = (change: number) => {
    const newParams = new URLSearchParams(params);
    newParams.set('offset', (offset + change * limit).toString());
    setParams(newParams);
  };

  if (!isResolved || isOrganizationSwitching) {
    return <Loader title="Getting dumpster types" />;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold mb-4">Dumpster Types</h2>
      <div className="flex justify-between items-center space-x-4">
        <div className="flex-grow">
          <Search
            value={search}
            onChange={handleSearch}
            placeholder="Search dumpster types by name or size"
            isLoading={containerTypeQuery.isLoading}
          />
        </div>
        <ContainerTypeSheet
          trigger={<Button className="text-white">New Dumpster Type</Button>}
          onSuccess={() => containerTypeQuery.refetch()}
        />
      </div>
      <div className="flex flex-col h-full">
        <DataTable
          columns={ContainerTypeColumns}
          data={containerTypes}
          onRowClick={(row) => setEditingContainerType(row)}
          sorting={sorting}
          onSortingChange={setSorting}
          onPageChange={handlePageChange}
          pageCount={Math.ceil((containerTypeQuery.data?.total || 0) / limit)}
          currentPage={currentPage}
          hasNextPage={(containerTypeQuery.data?.total ?? 0) > offset + limit}
          hasPreviousPage={offset > 0}
          total={containerTypeQuery.data?.total || 0}
          resourceName="container types"
          offset={offset}
          limit={limit}
        />
      </div>
      <ContainerTypeSheet
        containerType={editingContainerType}
        trigger={<></>}
        open={!!editingContainerType}
        onOpenChange={(open) => !open && setEditingContainerType(null)}
        onSuccess={() => {
          containerTypeQuery.refetch();
          setEditingContainerType(null);
        }}
      />
    </div>
  );
};

export default ContainerTypesTab;
